import { useCallback, useRef, useState, useEffect } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  computeRadiusStyle,
  computeContainerBorderRadiusCss,
  generateBatchTimelineHtml,
} from '../helpers/batchTimeline';
import type { Moment } from 'moment';
import type { ScaleFunction } from '@components/operatorPanel/interfaces';
import type { MachineEvent, WallboardBatch } from '@components/wallboard/types';
import { WithColor } from '@styles/colors';
import { useRealtimeTimelineUpdates } from '@components/machines/components/timeline/hooks/useRealtimeTimelineUpdates';
import { getMachineEventTimelineByBatchId } from '@reducers/machines';
import { toMachineEvent } from '@components/wallboard/helpers/batchTimeline';

export interface UseManageBatchTimeLineParams extends WithColor {
  batch: WallboardBatch | null;
  /** @deprecated No longer used — the hook imports getMachineEventTimelineByBatchId directly */
  getMachineEventTimelineByBatchId?: (filters: Record<string, unknown>, signal: AbortSignal) => Promise<any>;
  productionBatchId: number | null;
  status: string;
  height: number;
  borderRadius: number;
  roundOnlyTop: boolean;
  roundOnlyBottom: boolean;
  scale: ScaleFunction;
  localTime: (t?: string | Date | number | Moment) => Moment;
  withGaps: boolean;
}

export interface UseManageBatchTimeLineReturn {
  htmlSource: string;
  radiusStyle: Record<string, number>;
  containerBg: string;
}

const useManageBatchTimeLine = ({
  batch,
  productionBatchId,
  status,
  C,
  height,
  borderRadius,
  roundOnlyTop,
  roundOnlyBottom,
  scale,
  localTime,
  withGaps,
}: UseManageBatchTimeLineParams): UseManageBatchTimeLineReturn => {
  const { fetchTimelineWithRealtime } = useRealtimeTimelineUpdates();
  const abortSignal = useRef<AbortController>();
  const [machineEventData, setMachineEventData] = useState<MachineEvent[]>(batch?.demoMachineEvents || []);
  const machineEventDataRef = useRef(machineEventData);
  machineEventDataRef.current = machineEventData;

  const containerBg = C('Background/Light/Chart No Trans');
  const durationInMs = batch?.startedOn ? localTime().diff(batch.startedOn) : 0;
  const batchLoaded = !!batch;

  useEffect(() => {
    if (batch?.demoMachineEvents) {
      setMachineEventData(batch.demoMachineEvents);
    }
  }, [batch]);

  useFocusEffect(
    useCallback(() => {
      if (batch?.demoMachineEvents) return;
      if (!productionBatchId) return;
      if (!batchLoaded) return;

      if (abortSignal.current) abortSignal.current.abort();
      abortSignal.current = new AbortController();

      const cancelRealtime = fetchTimelineWithRealtime({
        productionBatchId,
        thunk: getMachineEventTimelineByBatchId(
          { productionBatchId, useAsyncFlowForRealtimeQuery: true, pagination: { pageNumber: 1, pageSize: 1000, includeCount: true } },
          abortSignal.current.signal
        ),
        processResponse: (machineEvents) => machineEvents.map(toMachineEvent),
        onInitialData: (data) => {
          setMachineEventData(data);
        },
        onRealtimeData: (data) => {
          setMachineEventData(data);
        },
        getCurrentEvents: () => ({
          loaded: true,
          events: machineEventDataRef.current as any,
          groupedByNCFile: [],
          groupedByBatch: [],
        }),
      });

      return () => { cancelRealtime(); };
    }, [productionBatchId, status, batch?.demoMachineEvents, batchLoaded])
  );

  const startTime = machineEventData.length > 0 ? localTime(machineEventData[0].startedOn) : localTime(new Date());
  const radiusStyle = computeRadiusStyle(roundOnlyTop, roundOnlyBottom, borderRadius, scale);
  const containerBorderRadiusCss = computeContainerBorderRadiusCss(roundOnlyTop, roundOnlyBottom, borderRadius);

  const htmlSource = generateBatchTimelineHtml({
    machineEventData,
    height,
    containerBorderRadiusCss,
    containerBg,
    durationInMs,
    startTime,
    localTimeFn: localTime,
    withGaps,
    roundOnlyTop,
    roundOnlyBottom,
    borderRadius,
    colorFn: C,
  });

  return { htmlSource, radiusStyle, containerBg };
};

export default useManageBatchTimeLine;
